import { useRef, useState, type DragEvent, type ChangeEvent } from 'react';
import { FiUpload, FiX, FiPaperclip, FiAlertCircle } from 'react-icons/fi';
import { cn } from '../lib/cn';

interface Props {
  arquivos: File[];
  onChange: (arquivos: File[]) => void;
  extensoesPermitidas?: string[];
  tamanhoMaxMB?: number;
  multiplo?: boolean;
  /** Número máximo de arquivos. */
  maxArquivos?: number;
  legenda?: string;
}

export function AnexoUpload({
  arquivos,
  onChange,
  extensoesPermitidas,
  tamanhoMaxMB = 20,
  multiplo = true,
  maxArquivos,
  legenda,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [arrastando, setArrastando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  function validar(novos: File[]): { ok: File[]; erros: string[] } {
    const ok: File[] = [];
    const erros: string[] = [];
    for (const a of novos) {
      const tamanhoMB = a.size / 1024 / 1024;
      if (tamanhoMB > tamanhoMaxMB) {
        erros.push(`"${a.name}" excede ${tamanhoMaxMB} MB.`);
        continue;
      }
      if (extensoesPermitidas && extensoesPermitidas.length > 0) {
        const ext = a.name.split('.').pop()?.toLowerCase() ?? '';
        const permitidas = extensoesPermitidas.map((e) => e.toLowerCase().replace(/^\./, ''));
        if (!permitidas.includes(ext)) {
          erros.push(`"${a.name}": extensão .${ext} não permitida (aceitas: ${permitidas.join(', ')}).`);
          continue;
        }
      }
      ok.push(a);
    }
    return { ok, erros };
  }

  function adicionar(novos: File[]) {
    const { ok, erros } = validar(novos);
    if (!multiplo) {
      if (erros.length > 0) setErro(erros.join(' ')); else setErro(null);
      onChange(ok.slice(0, 1));
      return;
    }
    let combinados = [...arquivos, ...ok];
    if (maxArquivos && combinados.length > maxArquivos) {
      combinados = combinados.slice(0, maxArquivos);
      erros.push(`Máximo de ${maxArquivos} arquivos.`);
    }
    setErro(erros.length > 0 ? erros.join(' ') : null);
    onChange(combinados);
  }

  function handleInput(e: ChangeEvent<HTMLInputElement>) {
    if (!e.target.files) return;
    adicionar(Array.from(e.target.files));
    e.target.value = '';
  }

  function handleDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setArrastando(false);
    if (e.dataTransfer.files) {
      adicionar(Array.from(e.dataTransfer.files));
    }
  }

  function remover(idx: number) {
    onChange(arquivos.filter((_, i) => i !== idx));
  }

  const accept = extensoesPermitidas?.map((e) => '.' + e.replace(/^\./, '')).join(',');

  return (
    <div>
      <div
        onDragOver={(e) => { e.preventDefault(); setArrastando(true); }}
        onDragLeave={() => setArrastando(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        className={cn(
          'border-2 border-dashed rounded-md p-6 text-center cursor-pointer transition',
          arrastando
            ? 'border-institutional bg-institutional/5'
            : 'border-slate-300 hover:border-slate-400 bg-slate-50',
        )}
      >
        <FiUpload className="mx-auto text-slate-400 mb-2" size={24} />
        <p className="text-sm text-slate-700">
          <span className="font-medium text-institutional">Clique para selecionar</span> ou arraste arquivos aqui
        </p>
        <p className="text-xs text-slate-500 mt-1">
          {legenda ?? `Até ${tamanhoMaxMB} MB${extensoesPermitidas && extensoesPermitidas.length > 0
            ? ` · ${extensoesPermitidas.map((e) => e.replace(/^\./, '').toUpperCase()).join(', ')}`
            : ''}`}
        </p>
        <input
          ref={inputRef}
          type="file"
          hidden
          multiple={multiplo}
          accept={accept}
          onChange={handleInput}
        />
      </div>

      {erro && (
        <div className="mt-2 flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
          <FiAlertCircle size={14} className="flex-shrink-0 mt-0.5" />
          <span>{erro}</span>
        </div>
      )}

      {arquivos.length > 0 && (
        <ul className="mt-3 space-y-2">
          {arquivos.map((a, idx) => (
            <li key={idx} className="flex items-center gap-3 px-3 py-2 bg-white border border-slate-200 rounded">
              <FiPaperclip className="text-slate-400 flex-shrink-0" size={14} />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-slate-900 truncate">{a.name}</div>
                <div className="text-xs text-slate-500">{(a.size / 1024).toFixed(1)} KB</div>
              </div>
              <button
                type="button"
                onClick={() => remover(idx)}
                className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition"
                title="Remover"
              >
                <FiX size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
